import {
    ChangeDetectionStrategy,
    Component, effect, inject, input, SecurityContext, signal,
    ViewEncapsulation
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { PlatformService, WINDOW } from '@seven1/angular/ssr';
import { Router } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { OrderFloatingButtonService } from '../../../services/order-floating-button.service';
import { TariffStore } from '../../../../../tariff';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, fromEvent, map } from 'rxjs';
import { ITariffOrderButtonBlok } from './tariff-order-button.model';
import { AsyncPipe, NgTemplateOutlet, TitleCasePipe } from '@angular/common';

@Component({
    selector: 'app-tariff-order-button',
    imports: [MatIconModule, TitleCasePipe, AsyncPipe, NgTemplateOutlet],
    templateUrl: './tariff-order-button.component.html',
    styleUrl: './tariff-order-button.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'px-5 bg-white' },
})
export class TariffOrderButtonComponent {
    private _window = inject(WINDOW);
    private _router = inject(Router);
    private _sanitizer = inject(DomSanitizer);
    private _platform = inject(PlatformService);
    button = inject(OrderFloatingButtonService);
    tariffStore = inject(TariffStore);

    block = input.required<ITariffOrderButtonBlok>();

    bottomPosition;
    sanitizedLink = signal<string | null>(null);

    constructor() {
        if (this._platform.isBrowser) {
            this.bottomPosition = toSignal(
                fromEvent(this._window!, 'scroll').pipe(
                    debounceTime(200),
                    map((ev) => {
                        return this.button.windowHeight - (this.button.windowHeight - this.button.targetEnd.getBoundingClientRect().top);
                    }),
                    takeUntilDestroyed()
                )
            );
        }
        effect(() => {
            if (this.bottomPosition) {
                const bottomPos = this.bottomPosition();
                if (
                    this.button.targetStart.getBoundingClientRect().top - (this._window?.innerHeight || 0) < 0 &&
                    (bottomPos || 0) > 1000
                ) {
                    this.button.activeSubject.next(true);
                } else {
                    this.button.activeSubject.next(false);
                }
            }
        });
        effect(() => {
            const block = this.block();
            if (block.link?.length) {
                this.sanitizedLink.set(this._sanitizer.sanitize(SecurityContext.URL, block.link));
            }
        });
    }

    public async scrollToTarget(): Promise<void> {
        await this._router.navigate([], { fragment: this.sanitizedLink() || undefined });
    }
}
